import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Person,
  Email,
  Phone,
  Business,
  Work
} from '@mui/icons-material';
import Layout from '../components/Layout/Layout';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { UserRole } from '../types';

interface Employee {
  id: string;
  name: string;
  email: string;
  position: string;
  employeeType: string;
  department: string;
  hourlyRate: number;
  status: string;
  joinDate: Date;
  phone: string;
  address: string;
  skills: string;
  agilePMUserId?: string;
  password?: string;
  mustChangePassword?: boolean;
}

const Employees: React.FC = () => {
  const { user, selectedOrganizationId } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  // Redirect ROOT_USER to root admin dashboard
  useEffect(() => {
    if (user?.role === UserRole.ROOT_USER) {
      navigate('/root-admin-dashboard', { replace: true });
    }
  }, [user?.role, navigate]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    position: '',
    employeeType: 'full-time',
    department: '',
    hourlyRate: 0,
    phone: '',
    address: '',
    skills: '',
    password: '',
    mustChangePassword: false
  });

  useEffect(() => {
    loadEmployees();
  }, [selectedOrganizationId]); // Reload when organization changes

  const loadEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // ROOT_USER MUST select organization first - PRIVACY PROTECTION
      if (user?.role === 'root_user' && !selectedOrganizationId) {
        console.warn('🔒 ROOT_USER must select organization first - blocking data load for privacy');
        setEmployees([]);
        setLoading(false);
        return; // BLOCK data loading until org selected
      }
      
      const data = await apiService.getEmployees();
      setEmployees(data);
    } catch (error) {
      console.error('Error loading employees:', error);
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (employee?: Employee) => {
    if (employee) {
      setSelectedEmployee(employee);
      setFormData({
        name: employee.name,
        email: employee.email,
        position: employee.position,
        employeeType: employee.employeeType,
        department: employee.department,
        hourlyRate: employee.hourlyRate,
        phone: employee.phone,
        address: employee.address,
        skills: employee.skills,
        password: '', // Clear password for edit
        mustChangePassword: employee.mustChangePassword || false
      });
    } else {
      setSelectedEmployee(null);
      setFormData({
        name: '',
        email: '',
        position: '',
        employeeType: 'full-time',
        department: '',
        hourlyRate: 0,
        phone: '',
        address: '',
        skills: '',
        password: '',
        mustChangePassword: false
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedEmployee(null);
  };

  const handleSubmit = async () => {
    try {
      console.log('🔍 Submitting employee form:', { selectedEmployee: !!selectedEmployee, formData });
      if (selectedEmployee) {
        console.log('🔍 Updating employee:', selectedEmployee.id);
        const result = await apiService.updateEmployee(selectedEmployee.id, formData);
        console.log('✅ Employee updated:', result);
      } else {
        console.log('🔍 Creating employee');
        const result = await apiService.createEmployee(formData, selectedOrganizationId || undefined);
        console.log('✅ Employee created:', result);
      }
      handleCloseDialog();
      await loadEmployees();
      console.log('✅ Employees reloaded');
    } catch (error) {
      console.error('❌ Error saving employee:', error);
      setError('Failed to save employee');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await apiService.deleteEmployee(id);
        loadEmployees();
      } catch (error) {
        console.error('Error deleting employee:', error);
        setError('Failed to delete employee');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const getEmployeeTypeColor = (type: string) => {
    switch (type) {
      case 'full-time':
        return 'primary';
      case 'part-time':
        return 'secondary';
      case 'contractor':
        return 'warning';
      case 'freelancer':
        return 'info';
      case 'intern':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner />
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            👥 Employees
          </Typography>
          {(user?.role === 'root_user' || user?.role === 'admin' || user?.role === 'hr_admin' || user?.role === 'super_manager') && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              Add Employee
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Quick Stats */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Employees
                </Typography>
                <Typography variant="h4">
                  {employees.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Active Employees
                </Typography>
                <Typography variant="h4">
                  {employees.filter(e => e.status === 'active').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Full-time
                </Typography>
                <Typography variant="h4">
                  {employees.filter(e => e.employeeType === 'full-time').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Contractors
                </Typography>
                <Typography variant="h4">
                  {employees.filter(e => e.employeeType === 'contractor').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Employees Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Position</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Hourly Rate</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Join Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.map((employee) => (
                  <TableRow key={employee.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                          {employee.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">
                            {employee.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {employee.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{employee.position}</TableCell>
                    <TableCell>
                      <Chip
                        label={employee.employeeType}
                        color={getEmployeeTypeColor(employee.employeeType) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{employee.department}</TableCell>
                    <TableCell>${employee.hourlyRate}/hr</TableCell>
                    <TableCell>
                      <Chip
                        label={employee.status}
                        color={getStatusColor(employee.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(employee.joinDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {(user?.role === 'root_user' || user?.role === 'admin' || user?.role === 'hr_admin' || user?.role === 'super_manager') && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(employee)}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(employee.id)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Add/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedEmployee ? 'Edit Employee' : 'Add New Employee'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Position"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Employee Type</InputLabel>
                  <Select
                    value={formData.employeeType}
                    onChange={(e) => setFormData({ ...formData, employeeType: e.target.value })}
                  >
                    <MenuItem value="full-time">Full-time</MenuItem>
                    <MenuItem value="part-time">Part-time</MenuItem>
                    <MenuItem value="contractor">Contractor</MenuItem>
                    <MenuItem value="freelancer">Freelancer</MenuItem>
                    <MenuItem value="intern">Intern</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Department"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Hourly Rate"
                  type="number"
                  value={formData.hourlyRate}
                  onChange={(e) => setFormData({ ...formData, hourlyRate: Number(e.target.value) })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={2}
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Skills"
                  multiline
                  rows={3}
                  value={formData.skills}
                  onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                />
              </Grid>
              {selectedEmployee ? (
                <>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.mustChangePassword || false}
                          onChange={(e) => setFormData({ ...formData, mustChangePassword: e.target.checked })}
                          name="mustChangePassword"
                          color="primary"
                        />
                      }
                      label="Change password on next login"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="New Password (leave empty to keep current)"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {selectedEmployee ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default Employees; 